import React, { useEffect, useState } from "react";
import { Button } from "@material-ui/core";
import { getGames } from "../../../../api/game";
import { getTexts, createText, updateText } from "../../../../api/text";
import { uploadSingleFile } from "../../../../api/files";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { API, SERVER_URL } from "../../../../api";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";

// canvaLink: enlace a la plantilla de Canva para cada imagen (solo no-avatar)
const IMAGE_KEYS = [
	{ key: "context.clock.image", label: "Context - Reloj (modal empezar juego)", order: 99, scenario: 0, defaultPath: null },
	{ key: "avatar.doctor", label: "Avatar Doctor", order: 100, scenario: 0, defaultPath: "uploads/avatars/default-doctor.png" },
	{ key: "avatar.patient", label: "Avatar Paciente", order: 101, scenario: 0, defaultPath: "uploads/avatars/default-patient.png" },
	{ key: "avatar.doctora", label: "Avatar Doctora", order: 102, scenario: 0, defaultPath: "uploads/avatars/default-doctora.png" },
	{ key: "avatar.intro.consultation", label: "Avatar Intro - Consulta", order: 103, scenario: 0, defaultPath: "uploads/avatars/intro-consultation.png" },
	{ key: "scenario2.intro.informe", label: "Escenario 2 Intro - Informe", order: 102, scenario: 2, defaultPath: "uploads/scenario2/informe.png" },
	{ key: "scenario2.result.table", label: "Escenario 2 Resultado - Tabla diagnóstico (modal correcto)", order: 103, scenario: 2, defaultPath: null},
	{ key: "scenario3.intro.image", label: "Escenario 3 Intro - Imagen (marco correo)", order: 104, scenario: 3, defaultPath: null },
	{ key: "scenario3.intro.modalImage", label: "Escenario 3 Intro - Modal instrucciones (batería 4 imágenes)", order: 105, scenario: 3, defaultPath: null },
	{ key: "scenario4.option1.image", label: "Escenario 4 - Opción 1 (Doble terapia) - Esquema", order: 105, scenario: 4, defaultPath: null },
	{ key: "scenario4.option2.image", label: "Escenario 4 - Opción 2 (Triple terapia) - Esquema", order: 106, scenario: 4, defaultPath: null },
	{ key: "scenario5.intro.image", label: "Escenario 5 (doble terapia) Intro - Analítica 3 meses", order: 107, scenario: 5, defaultPath: null },
	{ key: "scenario5.explanation.image", label: "Escenario 5 - Explanation - Esquema/gráfico", order: 108, scenario: 5, defaultPath: null},
	{ key: "scenario5.2.intro.image", label: "Escenario 5.2 - Intro - Esquema algoritmo", order: 109, scenario: 5, defaultPath: null },
	{ key: "scenario5.2.intro2.image", label: "Escenario 5.2 - Intro2 - Esquema (voclosporina)", order: 110, scenario: 5, defaultPath: null },
	{ key: "scenario5.2.end.image", label: "Escenario 5.2 - End - Imagen tratamiento", order: 111, scenario: 5, defaultPath: null },
	{ key: "qr.image", label: "QR como imagen", order: 112, scenario: 0, defaultPath: null },
];

function buildImageUrl(value) {
	if (!value || typeof value !== "string") return null;
	if (value.startsWith("http")) return value;
	return `${SERVER_URL.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
}

function ImagesPage() {
	const [games, setGames] = useState([]);
	const [selectedGameId, setSelectedGameId] = useState("");
	const [texts, setTexts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState({});

	useEffect(() => {
		let cancelled = false;
		getGames()
			.then((res) => {
				if (cancelled) return;
				if (res.status === 200 && res.data?.length) {
					setGames(res.data);
					const defaultGame = res.data.find((g) => g.default) || res.data[0];
					setSelectedGameId(defaultGame._id);
				}
			})
			.catch((err) => { if (!cancelled) alertError({ error: err, customMessage: "Could not get games." }); });
		return () => { cancelled = true; };
	}, []);

	useEffect(() => {
		if (!selectedGameId) return;
		let cancelled = false;
		setLoading(true);
		getTexts({ game: selectedGameId })
			.then((res) => {
				if (cancelled) return;
				if (res.status === 200 && Array.isArray(res.data)) setTexts(res.data || []);
				else setTexts([]);
			})
			.catch(() => { if (!cancelled) setTexts([]); })
			.finally(() => { if (!cancelled) setLoading(false); });
		return () => { cancelled = true; };
	}, [selectedGameId]);

	const getRecord = (key) => texts.find((t) => t.key === key);
	const getImageUrl = (key, defaultPath) => {
		const rec = getRecord(key);
		if (rec?.value) return buildImageUrl(rec.value);
		if (defaultPath) return `${SERVER_URL.replace(/\/$/, "")}/${defaultPath.replace(/^\//, "")}`;
		return null;
	};

	const handleFileChange = async (e, meta) => {
		const file = e.target?.files?.[0];
		const inputEl = document.getElementById(`file-${meta.key}`);
		if (!file || !selectedGameId) return;
		setUploading((prev) => ({ ...prev, [meta.key]: true }));
		try {
			const uploadRes = await uploadSingleFile(file, "avatars");
			if (uploadRes?.status !== 200 || !uploadRes.data?.fileURL) {
				throw new Error("Upload failed");
			}
			const filePath = uploadRes.data.fileURL;
			const record = getRecord(meta.key);
			if (record?._id) {
				await updateText(record._id, { value: filePath });
			} else {
				await createText({
					game: selectedGameId,
					key: meta.key,
					order: meta.order,
					scenario: meta.scenario,
					value: filePath,
				});
			}
			const res = await getTexts({ game: selectedGameId });
			if (res.status === 200 && Array.isArray(res.data)) setTexts(res.data);
			alertSuccess({ customMessage: "Imagen guardada." });
		} catch (err) {
			alertError({ error: err, customMessage: "No se pudo subir la imagen." });
		} finally {
			setUploading((prev) => ({ ...prev, [meta.key]: false }));
			if (inputEl) inputEl.value = "";
		}
	};

	return (
		<>
			<Card>
				<CardHeader title="Images" />
				<CardBody>
					<div className="form-group mb-4">
						<label htmlFor="images-game-select" className="font-weight-bold">
							Select a game
						</label>
						<select
							id="images-game-select"
							className="form-control"
							style={{ maxWidth: 320 }}
							value={selectedGameId}
							onChange={(e) => setSelectedGameId(e.target.value)}
						>
							{games.map((g) => (
								<option key={g._id} value={g._id}>{g.fullName}</option>
							))}
						</select>
						<small className="form-text text-muted">
							Avatar Paciente se reutiliza en todas las pantallas donde aparece el paciente.
						</small>
					</div>

					{loading ? (
						<p className="text-muted">Cargando…</p>
					) : (
						<div className="row">
							{[...IMAGE_KEYS]
								.sort((a, b) => {
									if (a.scenario !== b.scenario) return a.scenario - b.scenario;
									return (a.order || 0) - (b.order || 0) || (a.label || "").localeCompare(b.label || "");
								})
								.map((meta) => {
									const url = getImageUrl(meta.key, meta.defaultPath);
									const isUploading = uploading[meta.key];
									const isAvatar = meta.key.startsWith("avatar.");
									return (
										<div key={meta.key} className="col-md-6 col-lg-6 mb-4">
											<Card className="h-100">
												<CardBody>
													{!isAvatar && (
														<small className="text-muted d-block mb-1">
															{meta.scenario === 0 ? "Context" : `Escenario ${meta.scenario}`}
														</small>
													)}
													<label className="font-weight-bold d-block mb-2">{meta.label}</label>
													<div className={isAvatar ? "d-flex align-items-start" : ""}>
														{isAvatar ? (
															<>
																<div
																	className="rounded-circle overflow-hidden bg-light d-flex align-items-center justify-content-center flex-shrink-0"
																	style={{ width: 80, height: 80 }}
																>
																	{url ? (
																		<img
																			src={url}
																			alt={meta.label}
																			style={{ width: "100%", height: "100%", objectFit: "cover" }}
																		/>
																	) : (
																		<span className="text-muted small">Sin imagen</span>
																	)}
																</div>
																<div className="ml-3">
																	<input
																		type="file"
																		accept="image/*"
																		className="d-none"
																		id={`file-${meta.key}`}
																		onChange={(e) => handleFileChange(e, meta)}
																		disabled={isUploading}
																	/>
																	<label htmlFor={`file-${meta.key}`} className="mb-0">
																		<Button
																			variant="outlined"
																			color="primary"
																			size="small"
																			component="span"
																			disabled={isUploading}
																		>
																			{url ? "Cambiar" : "Subir"} imagen
																		</Button>
																	</label>
																	{isUploading && <span className="ml-2 small text-muted">Subiendo…</span>}
																</div>
															</>
														) : (
															<>
																<div
																	className="rounded overflow-hidden bg-light d-flex align-items-center justify-content-center mb-2"
																	style={{ minHeight: 160, maxHeight: 220 }}
																>
																	{url ? (
																		<img
																			src={url}
																			alt={meta.label}
																			style={{ maxWidth: "100%", width: "100%", maxHeight: 220, objectFit: "contain" }}
																		/>
																	) : (
																		<span className="text-muted small p-3">Sin imagen</span>
																	)}
																</div>
																<input
																	type="file"
																	accept="image/*"
																	className="d-none"
																	id={`file-${meta.key}`}
																	onChange={(e) => handleFileChange(e, meta)}
																	disabled={isUploading}
																/>
																<div className="d-flex flex-wrap align-items-center gap-2 mt-2">
																	<label htmlFor={`file-${meta.key}`} className="mb-0">
																		<Button
																			variant="outlined"
																			color="primary"
																			size="small"
																			component="span"
																			disabled={isUploading}
																		>
																			{url ? "Cambiar" : "Subir"} imagen
																		</Button>
																	</label>
																	{meta.canvaLink && (
																		<Button
																			variant="outlined"
																			size="small"
																			href={meta.canvaLink}
																			target="_blank"
																			rel="noopener noreferrer"
																			component="a"
																		>
																			Abrir Plantilla
																		</Button>
																	)}
																</div>
																{isUploading && <span className="ml-2 small text-muted">Subiendo…</span>}
															</>
														)}
													</div>
												</CardBody>
											</Card>
										</div>
									);
								})}
						</div>
					)}
				</CardBody>
			</Card>
		</>
	);
}

export default ImagesPage;
